import {
  atualizarExtraSchema,
  COBRANCAS_DE_EXTRA,
  type CobrancaDeExtra,
  criarExtraSchema,
  formatBRL,
  fromWire,
  ROTULO_DA_COBRANCA,
  ROTULO_DO_TIPO_DE_EXTRA,
  TIPO_DE_EXTRA_COM_FRANQUIA,
  TIPO_DE_EXTRA_COM_KM,
  type TipoDeExtra,
  TIPOS_DE_EXTRA,
  TIPOS_DE_EXTRA_COM_PRESTADOR,
  valorDoExtra,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { ROTAS_DE_EXTRAS } from '@/lib/endpoints'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { montarQuery } from '@/lib/query-string'

import { CHAVE_DE_COTACOES, type FiltroDeAtivo, paginaDe } from './api'

/**
 * Extras — o catálogo do que entra na conta além da diária, e o formulário que
 * o mantém.
 *
 * ---------------------------------------------------------------------------
 * POR QUE A CAMADA DE DADOS MORA NESTE ARQUIVO, E NÃO EM `./api`
 * ---------------------------------------------------------------------------
 * `./api` é a camada de PREÇO (tabelas, tarifas, as quatro regras, a política);
 * extra e cupom são catálogos independentes dela — não têm tabela, não têm
 * faixa, não passam pelas camadas. Colocá-los lá engordaria um arquivo que já
 * é grande com um domínio que não divide nada além do módulo.
 *
 * Ficam aqui, e não em `pages/extras-page.tsx`, porque `react-refresh` proíbe
 * export que não seja componente dentro de `src/pages/**` — e mesmo que
 * permitisse, o diálogo precisa das mutações e a página precisa da consulta:
 * um dos dois importaria do outro de qualquer jeito.
 *
 * ---------------------------------------------------------------------------
 * O TIPO DIZ O QUE A COISA É; A COBRANÇA DIZ QUANTO ELA CUSTA
 * ---------------------------------------------------------------------------
 * São dois campos porque são duas perguntas. Proteção e condutor adicional
 * cobram igual (`POR_DIARIA`) e não são nem de longe a mesma coisa; entrega
 * fora da base e cadeirinha cobram igual (`POR_RESERVA`) e também não.
 *
 * Quem multiplica é `valorDoExtra`, do motor determinístico (invariante nº 3):
 * `POR_DIARIA` vezes as diárias, `POR_UNIDADE` vezes a quantidade,
 * `POR_RESERVA` uma vez só. A prévia do formulário chama ESSA função — o
 * painel não reimplementa a conta, senão existiriam dois lugares onde a
 * proteção pode custar diferente do que a cotação cobrou.
 *
 * ---------------------------------------------------------------------------
 * FRANQUIA SÓ EM PROTEÇÃO, E `exigePrestador` É UMA PROMESSA DE ESCALA
 * ---------------------------------------------------------------------------
 * A franquia é o que distingue as três proteções entre si, e é recusada em
 * qualquer outro tipo — pelo Zod compartilhado, pelo serviço e pelo CHECK
 * `extras_franquia_so_em_protecao`. Por isso o campo some da tela fora de
 * `PROTECAO`, e a EDIÇÃO manda `franquia: null` explicitamente ao trocar de
 * tipo: `undefined` significa "não mexa", e não mexer aqui é 422 garantido.
 *
 * `exigePrestador` não é decoração nem um selo de qualidade: é o interruptor
 * que faz a cotação RECUSAR o extra quando não há motorista ou segurança livre
 * no período. Marcar sem escala é vender um serviço que some do orçamento sem
 * o cliente entender por quê — daí o aviso em voz alta no formulário.
 */

// ---------------------------------------------------------------------------
// Modelo de leitura
// ---------------------------------------------------------------------------

/**
 * Um extra como o painel o vê.
 *
 * `valorUnitario` e `franquia` são STRING de centavos (invariante nº 1): a
 * coluna é `BIGINT` e `JSON.stringify` de um `bigint` lança. Nenhum deles vira
 * `number` no caminho — a formatação é toda de `lib/dinheiro`.
 */
export const extraSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  descricao: z.string().nullable(),
  tipo: z.enum(TIPOS_DE_EXTRA),
  cobranca: z.enum(COBRANCAS_DE_EXTRA),
  /** Centavos como string decimal (o wire de `Money`). */
  valorUnitario: z.string(),
  /** Só em `PROTECAO`. `null` em todo o resto — é o CHECK da migration. */
  franquia: z.string().nullable(),
  /**
   * Km que cada unidade acrescenta à franquia da locação. Só em `KM_EXTRA`.
   * `.optional()` de propósito: o DTO de saída da API ainda não devolve o
   * campo — quando devolver, a edição passa a pré-preencher sozinha, e até lá
   * o parse não quebra.
   */
  kmPorUnidade: z.number().int().nullable().optional(),
  /** Teto por reserva. `null` = sem teto. */
  quantidadeMaxima: z.number().int().nullable(),
  /** A cotação recusa o extra quando não há prestador livre no período. */
  exigePrestador: z.boolean(),
  ordemDeExibicao: z.number().int(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Extra = z.infer<typeof extraSchema>

export const paginaDeExtrasSchema = paginaDe(extraSchema)

export type PaginaDeExtras = z.infer<typeof paginaDeExtrasSchema>

/**
 * Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio.
 *
 * Mapa aberto pelo mesmo motivo de `CorpoDeTabelaDePreco` em `./api`: os
 * schemas de entrada TRANSFORMAM (`moneySchemaNonNegative` devolve `bigint`), e
 * tipar aqui como `z.infer` mandaria `bigint` para o `JSON.stringify`.
 */
export type CorpoDeExtra = Readonly<Record<string, unknown>>

export interface FiltrosDeExtras {
  readonly pagina: number
  readonly tamanho: number
  /** Nome e descrição — é como o gestor procura "cadeirinha". */
  readonly busca: string
  readonly tipo: TipoDeExtra | ''
  readonly cobranca: CobrancaDeExtra | ''
  readonly exigePrestador: FiltroDeAtivo
  readonly ativo: FiltroDeAtivo
}

export const CHAVE_DE_EXTRAS = 'extras'

// ---------------------------------------------------------------------------
// Consulta e escrita
// ---------------------------------------------------------------------------

/**
 * Mexer no catálogo muda o ORÇAMENTO, não só a lista.
 *
 * Um extra desativado, um preço corrigido, um `exigePrestador` marcado: a
 * cotação que a tela está exibindo virou passado no instante do `PATCH`. A
 * cotação já EMITIDA não muda — `QuoteSnapshot` congela linha a linha —; o que
 * sai do cache é a que a tela acabou de pedir.
 */
function invalidarExtras(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_EXTRAS] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_COTACOES] })
}

export function useExtras(filtros: FiltrosDeExtras): UseQueryResult<PaginaDeExtras> {
  return useQuery({
    queryKey: [CHAVE_DE_EXTRAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        tipo: filtros.tipo,
        cobranca: filtros.cobranca,
        exigePrestador: filtros.exigePrestador,
        ativo: filtros.ativo,
      })

      return apiGet(`${ROTAS_DE_EXTRAS.lista}${query}`, paginaDeExtrasSchema, { signal })
    },
    // Sem isto a tabela pisca em branco a cada tecla digitada no filtro.
    placeholderData: (anterior) => anterior,
  })
}

export function useCriarExtra(): UseMutationResult<Extra, Error, CorpoDeExtra> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeExtra) => apiPost(ROTAS_DE_EXTRAS.lista, extraSchema, dados),
    onSuccess: () => {
      invalidarExtras(queryClient)
    },
  })
}

export interface AtualizacaoDeExtra {
  readonly id: string
  readonly dados: CorpoDeExtra
}

export function useAtualizarExtra(): UseMutationResult<Extra, Error, AtualizacaoDeExtra> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeExtra) =>
      apiPatch(ROTAS_DE_EXTRAS.porId(id), extraSchema, dados),
    onSuccess: () => {
      invalidarExtras(queryClient)
    },
  })
}

/**
 * `DELETE` marca `ativo: false`; a linha NÃO é apagada — a Fase 4 precisa do
 * nome do extra para escrever o contrato das reservas que já o contrataram.
 */
export function useDesativarExtra(): UseMutationResult<Extra, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_EXTRAS.porId(id), extraSchema),
    onSuccess: () => {
      invalidarExtras(queryClient)
    },
  })
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/**
 * O multiplicador da cobrança, dito por extenso.
 *
 * É a informação que decide o valor final e a que some quando a tela mostra só
 * "R$ 89,00": o mesmo preço unitário vira R$ 445 numa proteção de cinco diárias
 * e R$ 89 numa entrega fora da base. A frase vai ao lado do valor em toda
 * listagem, não escondida num `title`.
 */
export const MULTIPLICADOR_DA_COBRANCA: Readonly<Record<CobrancaDeExtra, string>> = {
  POR_DIARIA: 'por diária contratada',
  POR_RESERVA: 'uma vez na reserva',
  POR_UNIDADE: 'por unidade escolhida',
}

/** Diárias da prévia. Uma semana de fim de semana longo — o caso comum aqui. */
const DIARIAS_DA_PREVIA = 5

/**
 * Quanto o extra sai numa locação de referência, pela função do MOTOR.
 *
 * `valorDoExtra` é a mesma função que a cotação chama; reimplementar a
 * multiplicação aqui daria um número que "quase" bate, e o "quase" apareceria
 * como divergência entre o que o gestor cadastrou e o que o cliente pagou.
 *
 * `null` enquanto o valor digitado não der para ler — durante a digitação ele é
 * ilegível na maior parte do tempo, e prévia com número inventado é pior do que
 * prévia nenhuma.
 */
function previaDoExtra(valorEmReais: string, cobranca: CobrancaDeExtra): string | null {
  const wire = deCampoDeReaisParaWire(valorEmReais)

  if (wire === null) {
    return null
  }

  try {
    return formatBRL(
      valorDoExtra(
        {
          id: 'previa',
          nome: 'prévia',
          cobranca,
          valorUnitario: fromWire(wire),
          quantidade: 1,
        },
        DIARIAS_DA_PREVIA,
      ),
    )
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

const CAMPOS = [
  'nome',
  'descricao',
  'tipo',
  'cobranca',
  'valorUnitario',
  'franquia',
  'kmPorUnidade',
  'quantidadeMaxima',
  'ordemDeExibicao',
  'exigePrestador',
  'ativo',
] as const

interface FormularioDeExtra {
  nome: string
  descricao: string
  tipo: TipoDeExtra
  cobranca: CobrancaDeExtra
  /** Reais digitados (`"89,00"`), convertidos para centavos no envio. */
  valorUnitario: string
  /** Reais digitados. Só existe em `PROTECAO`. */
  franquia: string
  /** Inteiro digitado. Só existe em `KM_EXTRA` — e lá é obrigatório. */
  kmPorUnidade: string
  quantidadeMaxima: string
  ordemDeExibicao: string
  /**
   * Os dois campos que NÃO são string: `z.boolean()` não faz coerção, e
   * `"false"` chegaria no contrato como um booleano verdadeiro — o extra
   * voltaria à vitrine sozinho e a cotação passaria a exigir escala.
   */
  exigePrestador: boolean
  ativo: boolean
}

const TIPO_PADRAO: TipoDeExtra = TIPOS_DE_EXTRA[0]
const COBRANCA_PADRAO: CobrancaDeExtra = COBRANCAS_DE_EXTRA[0]

/** `0` é ordem válida — por isso o teste é de ausência, não de valor falsy. */
function campoDeInteiro(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor)
}

/** Inteiro digitado, ou `null` quando o campo está vazio (= sem teto). */
function inteiroOuNulo(texto: string): string | null {
  const limpo = texto.trim()

  return limpo.length === 0 ? null : limpo
}

function valoresIniciais(extra: Extra | undefined): FormularioDeExtra {
  return {
    nome: extra?.nome ?? '',
    descricao: extra?.descricao ?? '',
    tipo: extra?.tipo ?? TIPO_PADRAO,
    cobranca: extra?.cobranca ?? COBRANCA_PADRAO,
    valorUnitario: paraCampoDeReais(extra?.valorUnitario),
    franquia: paraCampoDeReais(extra?.franquia),
    kmPorUnidade: campoDeInteiro(extra?.kmPorUnidade),
    quantidadeMaxima: campoDeInteiro(extra?.quantidadeMaxima),
    ordemDeExibicao: campoDeInteiro(extra?.ordemDeExibicao ?? 0),
    exigePrestador: extra?.exigePrestador ?? false,
    ativo: extra?.ativo ?? true,
  }
}

export interface DialogoDeExtraProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cadastra; preenchido edita. */
  readonly extra?: Extra | undefined
}

export function DialogoDeExtra({ aberto, aoFechar, extra }: DialogoDeExtraProps): ReactElement {
  const editando = extra !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarExtra()
  const atualizar = useAtualizarExtra()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeExtra>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir
  // "editar" depois de "novo extra" mostraria os valores do formulário anterior.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(extra))
    }
  }, [aberto, extra, reset])

  const tipo = watch('tipo')
  const cobranca = watch('cobranca')
  const valorDigitado = watch('valorUnitario')
  const exigePrestador = watch('exigePrestador')

  const comFranquia = tipo === TIPO_DE_EXTRA_COM_FRANQUIA
  const comKm = tipo === TIPO_DE_EXTRA_COM_KM
  const dependeDeGente = TIPOS_DE_EXTRA_COM_PRESTADOR.includes(tipo)
  const ocupado = criar.isPending || atualizar.isPending

  const previa = useMemo(() => previaDoExtra(valorDigitado, cobranca), [valorDigitado, cobranca])

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const valorWire = deCampoDeReaisParaWire(valores.valorUnitario)

    if (valorWire === null) {
      setError('valorUnitario', {
        type: 'validate',
        message: 'valor inválido — use o formato 89,00',
      })
      return
    }

    // A franquia é RECUSADA fora de proteção — pelo Zod, pelo serviço e pelo
    // CHECK do banco. Fora de `PROTECAO` ela vai como `null` explícito, e não
    // ausente: `undefined` significa "não mexa", e trocar o tipo de um extra que
    // TINHA franquia sem limpá-la no mesmo corpo é 422 garantido.
    const franquiaWire = comFranquia ? deCampoDeReaisParaWire(valores.franquia) : null

    if (comFranquia && valores.franquia.trim().length > 0 && franquiaWire === null) {
      setError('franquia', { type: 'validate', message: 'valor inválido — use o formato 5.000,00' })
      return
    }

    const entrada = {
      nome: valores.nome,
      descricao: valores.descricao.trim().length > 0 ? valores.descricao : null,
      tipo: valores.tipo,
      cobranca: valores.cobranca,
      valorUnitario: valorWire,
      franquia: franquiaWire,
      // Mesmo desenho da franquia: fora de `KM_EXTRA` vai `null` EXPLÍCITO —
      // `undefined` significa "não mexa", e trocar o tipo de um extra que TINHA
      // km sem limpá-lo no mesmo corpo é 422 garantido
      // (`kmPorUnidadeCoerente`, dos dois lados do fio).
      kmPorUnidade: comKm ? valores.kmPorUnidade : null,
      quantidadeMaxima: inteiroOuNulo(valores.quantidadeMaxima),
      ordemDeExibicao: valores.ordemDeExibicao.trim().length > 0 ? valores.ordemDeExibicao : '0',
      exigePrestador: valores.exigePrestador,
      ativo: valores.ativo,
    }

    // Os três campos com `.default()` no contrato viajam SEMPRE preenchidos, e é
    // por isso que o `.partial()` da edição não morde: um `PATCH` sem eles
    // reativaria o extra desligado e desmarcaria a exigência de escala.
    const validado = editando
      ? atualizarExtraSchema.safeParse(entrada)
      : criarExtraSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    const aoDarCerto = (salvo: Extra): void => {
      toast.success(editando ? 'Extra atualizado' : 'Extra cadastrado', {
        description: salvo.ativo
          ? `${salvo.nome} aparece na vitrine, cobrado ${MULTIPLICADOR_DA_COBRANCA[salvo.cobranca]}.`
          : `${salvo.nome} ficou fora da vitrine.`,
      })
      aoFechar()
    }

    const aoFalhar = (causa: unknown): void => {
      setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
    }

    // O que viaja é `entrada`, e não `validado.data`: os dois schemas
    // transformam dinheiro em `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
    if (extra === undefined) {
      criar.mutate(entrada, { onSuccess: aoDarCerto, onError: aoFalhar })
      return
    }

    atualizar.mutate({ id: extra.id, dados: entrada }, { onSuccess: aoDarCerto, onError: aoFalhar })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar extra' : 'Novo extra'}</DialogTitle>
          <DialogDescription>
            O tipo diz o que a coisa é; a cobrança diz como ela é multiplicada na conta. Mudar o
            preço não mexe em cotação já emitida.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Nome"
              obrigatorio
              erro={errors.nome?.message}
              className="sm:col-span-2"
              dica="É o que o cliente lê na vitrine e o que sai escrito no contrato."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('nome', { required: 'informe o nome' })}
                  maxLength={120}
                  placeholder="Proteção completa"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Tipo" obrigatorio erro={errors.tipo?.message}>
              {(campo) => (
                <Select {...campo} {...register('tipo')} disabled={ocupado}>
                  {TIPOS_DE_EXTRA.map((item) => (
                    <option key={item} value={item}>
                      {ROTULO_DO_TIPO_DE_EXTRA[item]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo
              rotulo="Cobrança"
              obrigatorio
              erro={errors.cobranca?.message}
              dica={`Este extra é cobrado ${MULTIPLICADOR_DA_COBRANCA[cobranca]}.`}
            >
              {(campo) => (
                <Select {...campo} {...register('cobranca')} disabled={ocupado}>
                  {COBRANCAS_DE_EXTRA.map((item) => (
                    <option key={item} value={item}>
                      {ROTULO_DA_COBRANCA[item]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo
              rotulo="Valor unitário"
              obrigatorio
              erro={errors.valorUnitario?.message}
              dica="Em reais. Ex.: 89,00"
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('valorUnitario', {
                    required: 'informe o valor',
                    validate: (valor: string) =>
                      deCampoDeReaisParaWire(valor) !== null ||
                      'valor inválido — use o formato 89,00',
                  })}
                  inputMode="decimal"
                  placeholder="0,00"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Quantidade máxima"
              erro={errors.quantidadeMaxima?.message}
              dica="Teto por reserva. Vazio = sem teto."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('quantidadeMaxima')}
                  type="number"
                  min={1}
                  inputMode="numeric"
                  placeholder="sem teto"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          {/* A prévia usa a MESMA função do motor que a cotação chama. Ela é o
              único jeito de a cobrança deixar de ser uma palavra num select e
              virar o número que o cliente vai pagar. */}
          <Alert variant="info">
            <AlertTitle>Como este valor entra na conta</AlertTitle>
            <AlertDescription>
              O valor unitário é cobrado {MULTIPLICADOR_DA_COBRANCA[cobranca]}.
              {previa === null ? null : (
                <>
                  {' '}
                  Numa locação de <span className="tabular-money">
                    {DIARIAS_DA_PREVIA} diárias
                  </span>{' '}
                  com uma unidade contratada, sai por{' '}
                  <span className="tabular-money">{previa}</span>.
                </>
              )}
            </AlertDescription>
          </Alert>

          {comFranquia ? (
            <Campo
              rotulo="Franquia"
              erro={errors.franquia?.message}
              dica="O que o cliente paga em caso de sinistro. É o campo que distingue as três proteções entre si."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('franquia', {
                    validate: (valor: string) =>
                      valor.trim().length === 0 ||
                      deCampoDeReaisParaWire(valor) !== null ||
                      'valor inválido — use o formato 5.000,00',
                  })}
                  inputMode="decimal"
                  placeholder="0,00"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          ) : (
            <p className="text-xs text-fg-muted">
              Franquia só existe em extra do tipo{' '}
              {ROTULO_DO_TIPO_DE_EXTRA[TIPO_DE_EXTRA_COM_FRANQUIA]} — o banco recusa nos demais.
              Trocar para proteção libera o campo.
            </p>
          )}

          {/* Só em KM_EXTRA, e lá é OBRIGATÓRIO: pacote de km sem este número é
              dinheiro que entra e franquia que não cresce — o fechamento
              cobraria excedente de um km que o cliente comprou. */}
          {comKm ? (
            <Campo
              rotulo="Km por unidade"
              obrigatorio
              erro={errors.kmPorUnidade?.message}
              dica="Quantos km cada unidade contratada acrescenta à franquia da locação. É este número que o fechamento usa para não cobrar excedente de km comprado."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('kmPorUnidade', {
                    required: 'informe quantos km cada unidade acrescenta',
                    validate: (valor: string) => {
                      const numero = Number(valor)

                      return (
                        (Number.isInteger(numero) && numero >= 1) ||
                        'km por unidade precisa ser um inteiro maior que zero'
                      )
                    },
                  })}
                  type="number"
                  min={1}
                  inputMode="numeric"
                  placeholder="100"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          ) : null}

          <Campo
            rotulo="Descrição"
            erro={errors.descricao?.message}
            dica="Aparece no app, abaixo do nome. Diga o que está incluído, não o preço."
          >
            {(campo) => (
              <Textarea
                {...campo}
                {...register('descricao')}
                rows={3}
                maxLength={2000}
                disabled={ocupado}
              />
            )}
          </Campo>

          <fieldset className="space-y-2" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Vitrine e escala</legend>

            <Campo
              rotulo="Ordem de exibição"
              erro={errors.ordemDeExibicao?.message}
              dica="Menor aparece primeiro no app. Empate desempata pelo nome."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('ordemDeExibicao')}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="tabular-money"
                />
              )}
            </Campo>

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('exigePrestador')} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">
                  Depende de prestador livre
                </span>
                <span className="block text-xs text-fg-secondary">
                  Marcado, a cotação confere a escala do período e RECUSA o extra quando não há
                  motorista ou segurança disponível.
                </span>
              </span>
            </label>

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('ativo')} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">Na vitrine</span>
                <span className="block text-xs text-fg-secondary">
                  Desmarcado, o extra some do app sem sumir das reservas que já o contrataram.
                </span>
              </span>
            </label>
          </fieldset>

          {/* Âmbar semântico, nunca o amarelo da marca: isto é aviso, e o
              amarelo aqui é identidade. */}
          {exigePrestador ? (
            <Alert variant="warning">
              <AlertTitle>Este extra será recusado sem gente na escala</AlertTitle>
              <AlertDescription>
                A cotação procura um prestador livre no período e, se não achar, devolve o extra
                como recusado — o cliente vê o orçamento sem ele. Cadastre a equipe e mantenha a
                escala em dia antes de anunciar o serviço.
              </AlertDescription>
            </Alert>
          ) : dependeDeGente ? (
            <Alert variant="warning">
              <AlertTitle>
                {ROTULO_DO_TIPO_DE_EXTRA[tipo]} costuma depender de gente livre
              </AlertTitle>
              <AlertDescription>
                Sem marcar “depende de prestador livre”, a cotação vende este serviço sem conferir a
                escala — e alguém descobre no balcão que não há quem execute.
              </AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Cadastrar extra'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
